import dayjs, { Dayjs } from 'dayjs';
import { ApolloError } from '@apollo/client';

import { describeRateLimitRejection, getRateLimitRejection } from '../../../common/rateLimit';
import { checkRecordingUrl } from './recording';

/**
 * The log-a-call form (card 21) as plain data and pure functions, tested
 * without rendering: the outcomes and their keys, follow-up dates, the
 * duration as typed, the checks the API makes (crm-calls.ts newCallFields)
 * made before sending, and what its refusals mean.
 *
 * Backend reference: ilbp-backend docs/CARD_16_CALL_LOGGING.md §2–§4 (the API)
 * and CARD_21_LOG_A_CALL_AND_QUEUE.md (these screens).
 */

export type CallOutcome =
  | 'ANSWERED'
  | 'NOT_HOME'
  | 'LEFT_MESSAGE'
  | 'SPOKE'
  | 'REFUSED'
  | 'BAD_INFO'
  | 'SEND_EMAIL'
  | 'SEND_TEXT'
  | 'INBOUND';
export type CallDirection = 'OUTBOUND' | 'INBOUND';
export type CallMethod = 'PHONE' | 'WHATSAPP' | 'SMS' | 'EMAIL' | 'IN_PERSON' | 'OTHER';

export type FollowUpPresetId = 'tomorrow' | 'in3days' | 'inWeek' | 'in2weeks' | 'inMonth';

export interface OutcomeChoice {
  value: CallOutcome;
  /** The number key that picks it. */
  key: string;
  label: string;
  hint: string;
  /** The follow-up an outcome like this usually wants: chosen for the caller until they choose. */
  followUp?: FollowUpPresetId;
}

/** In the order a caller meets them; keys 1 to 9. UNKNOWN is the history's and never offered. */
export const OUTCOMES: Array<OutcomeChoice> = [
  { value: 'ANSWERED', key: '1', label: 'Answered', hint: 'They picked up' },
  { value: 'NOT_HOME', key: '2', label: 'No answer', hint: 'Nobody picked up, no message left', followUp: 'tomorrow' },
  { value: 'LEFT_MESSAGE', key: '3', label: 'Left message', hint: 'Voicemail, or a message with someone', followUp: 'in3days' },
  { value: 'SPOKE', key: '4', label: 'Spoke', hint: 'A real conversation' },
  { value: 'REFUSED', key: '5', label: 'Refused', hint: 'Did not want to talk' },
  { value: 'BAD_INFO', key: '6', label: 'Wrong number', hint: 'Wrong or dead number' },
  { value: 'SEND_EMAIL', key: '7', label: 'Send email', hint: 'Asked for an email instead' },
  { value: 'SEND_TEXT', key: '8', label: 'Send text', hint: 'Asked for a text instead' },
  { value: 'INBOUND', key: '9', label: 'They called', hint: 'They rang us' },
];

export const outcomeByKey = (key: string) => OUTCOMES.find((outcome) => outcome.key === key);
export const outcomeLabel = (value: string) => OUTCOMES.find((outcome) => outcome.value === value)?.label ?? value;

export const METHODS: Array<{ value: CallMethod; label: string }> = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'SMS', label: 'Text' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'IN_PERSON', label: 'In person' },
  { value: 'OTHER', label: 'Other' },
];

/** NationBuilder's support levels, as the contact list words them. */
export const SUPPORT_LEVELS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Strong support' },
  { value: 2, label: 'Weak support' },
  { value: 3, label: 'Undecided' },
  { value: 4, label: 'Weak opposition' },
  { value: 5, label: 'Strong opposition' },
];

/** Follow-ups land at 10:00 on the day, the caller's time. */
const at10 = (day: Dayjs) => day.hour(10).minute(0).second(0).millisecond(0);

export const FOLLOW_UP_PRESETS: Array<{ id: FollowUpPresetId; label: string; at: (now: Dayjs) => Dayjs }> = [
  { id: 'tomorrow', label: 'Tomorrow', at: (now) => at10(now.add(1, 'day')) },
  { id: 'in3days', label: 'In 3 days', at: (now) => at10(now.add(3, 'day')) },
  { id: 'inWeek', label: 'In a week', at: (now) => at10(now.add(7, 'day')) },
  { id: 'in2weeks', label: 'In 2 weeks', at: (now) => at10(now.add(14, 'day')) },
  { id: 'inMonth', label: 'In a month', at: (now) => at10(now.add(1, 'month')) },
];

export type FollowUpChoice =
  | { kind: 'none' }
  /** `suggested`: picked by the outcome, not the caller, so a new outcome may change it. */
  | { kind: 'preset'; id: FollowUpPresetId; suggested: boolean }
  /** A `datetime-local` value: "2026-10-20T14:30". */
  | { kind: 'custom'; at: string };

export interface CallForm {
  outcome: CallOutcome | null;
  notes: string;
  /** As typed: "4:30", "4", "90s". */
  duration: string;
  direction: CallDirection;
  method: CallMethod;
  /** Empty: now. Otherwise a `datetime-local` value, for a call logged after the fact. */
  occurredAt: string;
  followUp: FollowUpChoice;
  supportLevel: number | null;
  /** They asked not to be called again. Only ever sets it. */
  doNotCall: boolean;
  /** A link to the call's recording, as typed (card 21.7). Empty: none. */
  recordingUrl: string;
}

export const emptyForm = (direction: CallDirection = 'OUTBOUND'): CallForm => ({
  outcome: null,
  notes: '',
  duration: '',
  direction,
  method: 'PHONE',
  occurredAt: '',
  followUp: { kind: 'none' },
  supportLevel: null,
  doNotCall: false,
  recordingUrl: '',
});

/**
 * Picks an outcome. "They called" makes the call inbound. A follow-up the
 * caller did not choose follows the outcome: "No answer" suggests tomorrow,
 * "Answered" none. One they chose is kept.
 */
export const chooseOutcome = (form: CallForm, outcome: CallOutcome): CallForm => {
  const choice = OUTCOMES.find((o) => o.value === outcome);
  const callerChose = form.followUp.kind === 'custom' || (form.followUp.kind === 'preset' && !form.followUp.suggested);
  return {
    ...form,
    outcome,
    direction: outcome === 'INBOUND' ? 'INBOUND' : form.direction,
    followUp: callerChose
      ? form.followUp
      : choice?.followUp
        ? { kind: 'preset', id: choice.followUp, suggested: true }
        : { kind: 'none' },
  };
};

/* ------------------------------------------------------------------ *
 * Duration
 * ------------------------------------------------------------------ */

/** The API's limit: a day. */
export const MAX_DURATION_SEC = 86_400;

/**
 * Seconds from what a caller types, or null for nothing, or NaN when it cannot
 * be read. A bare number is minutes ("5"), as callers say it; "4:30" is
 * minutes and seconds, "1:02:30" hours too; "90s", "4m", "4m 30s", "1h 5m" read
 * as written.
 */
export const parseDuration = (text: string): number | null => {
  const value = text.trim().toLowerCase();
  if (!value) return null;
  if (/^\d+(\.\d+)?$/.test(value)) return Math.round(Number(value) * 60);
  const clock = value.match(/^(?:(\d+):)?(\d{1,3}):([0-5]\d)$/);
  if (clock) {
    const [, h, m, s] = clock;
    return Number(h ?? 0) * 3600 + Number(m) * 60 + Number(s);
  }
  const units = value.match(/^(?:(\d+)\s*h(?:ours?|rs?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?|s)?)?)?\s*(?:(\d+)\s*s(?:ec(?:onds?|s)?)?)?$/);
  if (units && (units[1] || units[2] || units[3])) {
    return Number(units[1] ?? 0) * 3600 + Number(units[2] ?? 0) * 60 + Number(units[3] ?? 0);
  }
  return NaN;
};

/** "0:45", "4:30", "1:02:03". */
export const formatDuration = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};

/* ------------------------------------------------------------------ *
 * Checks, as the API makes them (crm-calls.ts newCallFields)
 * ------------------------------------------------------------------ */

export const NOTES_MAX = 10_000;
/** A call "now" from a clock a little ahead of the server's is still now. */
const CLOCK_SKEW_MS = 5 * 60_000;
const FOLLOW_UP_MAX_DAYS = 730;

export type CallField = 'outcome' | 'notes' | 'duration' | 'occurredAt' | 'followUp' | 'recordingUrl';
export type CallProblems = Partial<Record<CallField, string>>;

/** When the call happened: now, unless it is being logged after the fact. */
export const occurredAtOf = (form: CallForm, now: Dayjs): Dayjs => (form.occurredAt ? dayjs(form.occurredAt) : now);

/** The follow-up's moment, or null for none. */
export const followUpAtOf = (form: CallForm, now: Dayjs): Dayjs | null => {
  const choice = form.followUp;
  if (choice.kind === 'none') return null;
  if (choice.kind === 'custom') return choice.at ? dayjs(choice.at) : null;
  const preset = FOLLOW_UP_PRESETS.find((p) => p.id === choice.id);
  return preset ? preset.at(occurredAtOf(form, now)) : null;
};

/** Everything wrong with the form, by field; empty when it can be sent. */
export const checkCall = (form: CallForm, now: Dayjs): CallProblems => {
  const problems: CallProblems = {};
  if (!form.outcome) problems.outcome = 'Choose what happened.';
  if (form.notes.trim().length > NOTES_MAX) {
    problems.notes = `Notes are at most ${NOTES_MAX.toLocaleString('en-US')} characters.`;
  }
  const seconds = parseDuration(form.duration);
  if (seconds !== null && Number.isNaN(seconds)) problems.duration = 'Write minutes, or minutes and seconds such as 4:30.';
  else if (seconds !== null && seconds > MAX_DURATION_SEC) problems.duration = 'At most 24 hours.';

  const occurred = occurredAtOf(form, now);
  if (!occurred.isValid()) problems.occurredAt = 'Not a valid date and time.';
  else if (occurred.valueOf() > now.valueOf() + CLOCK_SKEW_MS) problems.occurredAt = 'A call cannot be logged before it happens.';
  else if (occurred.year() < 2000) problems.occurredAt = 'Before 2000: check the year.';

  if (form.followUp.kind === 'custom' && !form.followUp.at) problems.followUp = 'Pick the date to call again.';
  const followUp = followUpAtOf(form, now);
  if (followUp && occurred.isValid()) {
    if (!followUp.isValid()) problems.followUp = 'Not a valid date and time.';
    else if (followUp.valueOf() <= occurred.valueOf()) problems.followUp = 'A follow-up comes after the call.';
    else if (followUp.valueOf() > now.add(FOLLOW_UP_MAX_DAYS, 'day').valueOf()) problems.followUp = 'At most two years ahead.';
  }
  const recording = checkRecordingUrl(form.recordingUrl);
  if (recording.problem) problems.recordingUrl = recording.problem;
  return problems;
};

export interface LogCallInput {
  contactId: number;
  outcome: CallOutcome;
  method: CallMethod;
  direction: CallDirection;
  notes?: string;
  durationSec?: number;
  supportLevel?: number;
  occurredAt?: string;
  followUpAt?: string;
  doNotCall?: true;
  recordingUrl?: string;
}

/** `crmLogCall`'s input. Call it on a form `checkCall` passed. Left out: what was not given. */
export const toLogCallInput = (contactId: number, form: CallForm, now: Dayjs): LogCallInput => {
  const notes = form.notes.trim();
  const seconds = parseDuration(form.duration);
  const followUp = followUpAtOf(form, now);
  const recording = checkRecordingUrl(form.recordingUrl).url;
  return {
    contactId,
    outcome: form.outcome as CallOutcome,
    method: form.method,
    direction: form.direction,
    ...(notes ? { notes } : {}),
    ...(seconds !== null && !Number.isNaN(seconds) ? { durationSec: seconds } : {}),
    ...(form.supportLevel ? { supportLevel: form.supportLevel } : {}),
    ...(form.occurredAt ? { occurredAt: dayjs(form.occurredAt).toISOString() } : {}),
    ...(followUp ? { followUpAt: followUp.toISOString() } : {}),
    ...(form.doNotCall ? { doNotCall: true as const } : {}),
    ...(recording ? { recordingUrl: recording } : {}),
  };
};

/** "Tue 20 Oct, 10:00": when a follow-up lands, the year only when it is not this one. */
export const formatFollowUp = (at: Dayjs, now: Dayjs) => at.format(at.isSame(now, 'year') ? 'ddd D MMM, HH:mm' : 'ddd D MMM YYYY, HH:mm');

/* ------------------------------------------------------------------ *
 * The API's refusals
 * ------------------------------------------------------------------ */

const FIELDS: Record<string, CallField> = {
  'input.outcome': 'outcome',
  'input.notes': 'notes',
  'input.durationSec': 'duration',
  'input.occurredAt': 'occurredAt',
  'input.followUpAt': 'followUp',
  'input.recordingUrl': 'recordingUrl',
};

export interface CallError {
  message: string;
  /** The input to mark, when the API named one. */
  field?: CallField;
  /** Do not call or do not contact: an outbound call cannot be logged, an inbound one can. */
  blocked?: boolean;
}

/** What a refused `crmLogCall` means, in the form's words. The notes are never lost: the form keeps them. */
export const describeCallError = (error: ApolloError | Error): CallError => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return { message: describeRateLimitRejection(rejection) };
  const graphQL = (error as ApolloError).graphQLErrors?.[0];
  const extensions = (graphQL?.extensions ?? {}) as Record<string, unknown>;
  switch (extensions.code) {
    case 'CRM_CONTACT_DO_NOT_CALL':
      return {
        blocked: true,
        message: extensions.doNotContact
          ? 'They are marked do not contact, so an outbound call cannot be logged. Nothing was saved. If they rang you, log it as inbound.'
          : 'They are marked do not call, so an outbound call cannot be logged. Nothing was saved. If they rang you, log it as inbound.',
      };
    case 'CRM_CONTACT_MERGED':
      return { message: `This record was merged into contact #${extensions.mergedIntoId}. Log the call there.` };
    case 'CRM_CONTACT_NOT_FOUND':
      return { message: 'This contact no longer exists. Nothing was saved.' };
    case 'CRM_INVALID_QUERY': {
      const field = FIELDS[String(extensions.field)];
      return { message: graphQL?.message ?? 'The API refused the call.', ...(field ? { field } : {}) };
    }
    default:
      return { message: 'The call could not be logged. Your notes are still here: try again.' };
  }
};

/* ------------------------------------------------------------------ *
 * A draft per person, so a closed dialog or a reload loses nothing
 * ------------------------------------------------------------------ */

const DRAFT_KEY = 'ilbp.crm.callDraft.';
/** Older drafts are about another call. */
const DRAFT_HOURS = 12;

/** Worth keeping: something the caller typed or chose. */
const hasContent = (form: CallForm) => Boolean(form.outcome || form.notes.trim() || form.duration.trim() || form.recordingUrl.trim());

export const readDraft = (contactId: number, now = Date.now()): CallForm | null => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(`${DRAFT_KEY}${contactId}`) ?? 'null');
    if (!stored || typeof stored.savedAt !== 'number' || now - stored.savedAt > DRAFT_HOURS * 3_600_000) return null;
    return { ...emptyForm(), ...stored.form };
  } catch {
    return null;
  }
};

export const writeDraft = (contactId: number, form: CallForm, now = Date.now()) => {
  try {
    if (hasContent(form)) window.localStorage.setItem(`${DRAFT_KEY}${contactId}`, JSON.stringify({ savedAt: now, form }));
    else window.localStorage.removeItem(`${DRAFT_KEY}${contactId}`);
  } catch {
    // Private windows and blocked storage: the draft lasts while the form is open.
  }
};

export const clearDraft = (contactId: number) => {
  try {
    window.localStorage.removeItem(`${DRAFT_KEY}${contactId}`);
  } catch {
    // As above.
  }
};

/** The logged call, as a toast says it: "Logged: No answer, 0:45. Follow up Tue 14 Oct, 10:00." */
export const describeLogged = (
  call: { outcome: string; durationSec: number | null; followUpAt: string | null; setDoNotCall: boolean },
  now: Dayjs,
) => {
  const parts = [outcomeLabel(call.outcome), call.durationSec ? formatDuration(call.durationSec) : ''].filter(Boolean);
  const followUp = call.followUpAt ? ` Follow up ${formatFollowUp(dayjs(call.followUpAt), now)}.` : '';
  const dnc = call.setDoNotCall ? ' Marked do not call.' : '';
  return `Logged: ${parts.join(', ')}.${followUp}${dnc}`;
};
